import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/* The header a page hands to the navbar. On mobile the navbar is the only chrome a page gets,
   so pages publish their title (and, where they have one, a back action) here instead of
   spending vertical space on a heading of their own. Desktop keeps its in-page headings. */
export type PageHeader = {
  title?: string;
  onBack?: () => void;
};

type PageHeaderContextValue = {
  header: PageHeader;
  setHeader: React.Dispatch<React.SetStateAction<PageHeader>>;
};

const PageHeaderContext = createContext<PageHeaderContextValue>({
  header: {},
  setHeader: () => {},
});

export const PageHeaderProvider = ({ children }: { children: React.ReactNode }) => {
  const [header, setHeader] = useState<PageHeader>({});
  const value = useMemo(() => ({ header, setHeader }), [header]);

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
};

/* Read side — used by the navbar. Outside a provider (the standalone children page) this is
   simply empty, and the navbar renders as it always did. */
export const usePageHeaderValue = () => useContext(PageHeaderContext).header;

/**
 * Write side — a page declares the title/back action the navbar should show while it is
 * mounted. `onBack` must be stable (wrap it in `useCallback`) or the effect re-runs each
 * render.
 */
export const usePageHeader = ({ title, onBack }: PageHeader) => {
  const { setHeader } = useContext(PageHeaderContext);

  useEffect(() => {
    setHeader({ title, onBack });
    return () => setHeader({});
  }, [title, onBack, setHeader]);
};
